import { Injectable } from "@angular/core";
import { BehaviorSubject, forkJoin, of } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { EnvironmentService } from "./environment.service";
import { HttpUtilService } from "./http-util.service";
import { NotificationsService } from "./notifications.service";
import cloneDeep from "lodash/cloneDeep";

@Injectable({
  providedIn: "root",
})
export class PauseIntegrationsService {
  private _integrationsState: BehaviorSubject<{
    integrations: SimpleIntegration[];
    isLoading: boolean;
    hasError: boolean;
  }> = new BehaviorSubject({
    integrations: [],
    isLoading: true,
    hasError: false,
  });
  private integrations$ = this._integrationsState.asObservable();
  public initialIntegrationLoad = true;
  constructor(
    private notification: NotificationsService,
    private http: HttpUtilService,
    private environementService: EnvironmentService
  ) {}
  getIntegrations$() {
    if (this.initialIntegrationLoad) {
      this._integrationsState.next({
        isLoading: true,
        integrations: [],
        hasError: false,
      });
      this.initialIntegrationLoad = false
      this.getIntegrationsNetworkCall();
    }
    return this.integrations$;
  }

  getIntegrationsNetworkCall() {
    this.http
      .request(
        "get",
        `${this.environementService.environment.apiRoot}/integrations`
      )
      .pipe(catchError((error) => of({ error })))
      .subscribe((response) => {
        const integrations = Array.isArray(response) ? response : [];
        const hasError = !!response?.error || !Array.isArray(response);
        this._integrationsState.next({
          isLoading: false,
          integrations,
          hasError,
        });
      });
  }

  updateAllIntegrationsPauseState(paused: boolean) {
    const integrations = this._integrationsState
      .getValue()
      .integrations.map((integration) => ({
        ...integration,
        isUpdating: paused !== integration.paused,
      }));
    this._integrationsState.next({
      ...this._integrationsState.getValue(),
      integrations,
    });

    const updatingIntegrations = integrations.filter(
      (integration) => integration.isUpdating
    );

    const requests = updatingIntegrations.map((integration) =>
      this.updateIntegrationPauseStateNetworkRequest$(integration, paused)
    );

    forkJoin(requests).subscribe();
  }

  updateIntegrationPauseState(integration: SimpleIntegration, paused: boolean) {
    const integrations = this._integrationsState
      .getValue()
      .integrations.map((_integration: SimpleIntegration) =>
        _integration.id !== integration.id
          ? _integration
          : { ...integration, isUpdating: true }
      );

    this._integrationsState.next({
      ...this._integrationsState.getValue(),
      integrations,
    });
    this.updateIntegrationPauseStateNetworkRequest$(
      integration,
      paused
    ).subscribe(() => {});
  }

  updateIntegrationPauseStateNetworkRequest$(
    integration: SimpleIntegration,
    paused: boolean
  ) {
    return this.http
      .request(
        "PUT",
        `${this.environementService.environment.apiRoot}/integrations`,
        { paused, id: integration.id },
        null,
        true
      )
      .pipe(catchError((error) => of({ error })))
      .pipe(
        map((response) => {
          const hasError = response?.error || response.errorMessage;
          if (hasError)
            this.notification.popNotification(
              "",
              "Error Updating Integration Paused State: " +
                integration.friendly_name ||
                integration.name ||
                "Unknown"
            );

          const integrations = this._integrationsState
            .getValue()
            .integrations.map((_integration: SimpleIntegration) =>
              _integration.id !== integration.id
                ? _integration
                : {
                    ...integration,
                    isUpdating: false,
                    paused: hasError ? _integration.paused : paused,
                  }
            );
          this._integrationsState.next({
            ...this._integrationsState.getValue(),
            integrations,
          });

          return { hasError, integration };
        })
      );
  }
}

export interface SimpleIntegration {
  id: string;
  name: string;
  paused: boolean;
  integration_type: string;
  institution_id: string;
  institution_name: string;
  friendly_name: string;
  url: string;
  lms_type: string;
  isUpdating?: boolean;
}
